import { categories, movies, type Category, type Movie } from "@/mocks/movies";

export const PAGE_SIZE = 12;

export function getCategoryBySlug(slug?: string): Category | undefined {
  return categories.find((item) => item.slug === slug);
}

export function getFeaturedMovies(): Movie[] {
  const featured = movies.filter((item) => item.featured);
  return featured.length > 0 ? featured.slice(0, 5) : movies.slice(0, 5);
}

export function getPopularMovies(limit = 5): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestMovies(): Movie[] {
  return [...movies].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.releaseDate.localeCompare(a.releaseDate);
  });
}

export function getMovieById(id?: string): Movie | undefined {
  return movies.find((item) => item.id === id);
}

export function getMoviesByCategory(slug?: string): Movie[] {
  if (!slug) return [];
  return movies.filter((item) => item.genreSlugs.includes(slug));
}

export function getRelatedMovies(movie: Movie, limit = 5): Movie[] {
  const sameGenre = movies.filter(
    (item) =>
      item.id !== movie.id &&
      item.genreSlugs.some((slug) => movie.genreSlugs.includes(slug)),
  );
  if (sameGenre.length >= limit) {
    return sameGenre
      .sort((a, b) => b.views - a.views)
      .slice(0, limit);
  }
  const rest = movies
    .filter((item) => item.id !== movie.id && !sameGenre.includes(item))
    .sort((a, b) => b.views - a.views);
  return [...sameGenre, ...rest].slice(0, limit);
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      item.cast.join(" "),
      item.genres.join(" "),
      item.country,
      String(item.year),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function sortMovies(list: Movie[], sort: string): Movie[] {
  const arr = [...list];
  if (sort === "hot") return arr.sort((a, b) => b.views - a.views);
  if (sort === "rating") return arr.sort((a, b) => b.rating - a.rating);
  return arr.sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.releaseDate.localeCompare(a.releaseDate);
  });
}

export function getYears(): number[] {
  const set = new Set(movies.map((item) => item.year));
  return Array.from(set).sort((a, b) => b - a);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return String(views);
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h} 小时 ${m} 分钟` : `${m} 分钟`;
}

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    currentPage: current,
    total: list.length,
  };
}

export { categories, movies };
export type { Category, Movie };